import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DISTRIBUTION_PROCESS_STATUS,
  DISTRIBUTION_TYPE,
} from "@/constants/distributionStatus";
import {
  Check,
  ChevronDown,
  ExternalLink,
  Image as ImageIcon,
  X,
} from "lucide-react";
import {
  ACTION_BUTTON,
  BADGE_BASE,
  BADGE_NEUTRAL,
  BADGE_PRE_SALE,
  CARD_SURFACE,
  SURFACE_CARD,
  SURFACE_INSET,
  badgeStatus,
  plural,
} from "../styles";
import { DistributionFileType, DistributionItemType } from "../types";

type DistributionCardProps = {
  item: DistributionItemType;
  files: DistributionFileType[];
  isLoadingFiles: boolean;
  isSelected: boolean;
  isProcessing: boolean;
  onToggle: (uuid: string) => void;
  onAccept: (item: DistributionItemType) => void;
  onReject: (item: DistributionItemType) => void;
};

const formatDate = (value?: string | null) => {
  if (!value) return null;

  return new Date(value).toLocaleString("pt-BR");
};

const InfoField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <span className="block text-xs font-medium text-muted-foreground">
      {label}
    </span>
    <span className="text-sm text-foreground">{children}</span>
  </div>
);

export const DistributionCard = ({
  item,
  files,
  isLoadingFiles,
  isSelected,
  isProcessing,
  onToggle,
  onAccept,
  onReject,
}: DistributionCardProps) => {
  const [filesOpen, setFilesOpen] = useState(false);

  const isPending = item.statusId === DISTRIBUTION_PROCESS_STATUS.PENDING;
  const isAccepted = item.statusId === DISTRIBUTION_PROCESS_STATUS.ACCEPTED;
  const isRejected = item.statusId === DISTRIBUTION_PROCESS_STATUS.REJECTED;
  const isPreSale = item.distributionTypeId === DISTRIBUTION_TYPE.PRE_SALE;

  // Pré-venda aceita não passa por um usuário: o sistema aceita sozinho.
  const processedBy = isPreSale && isAccepted ? "AUTOMATICAMENTE" : item.updatedByName;
  const showProcessedBy = Boolean(item.updatedByName) || (isPreSale && isAccepted);

  const surface = isSelected
    ? CARD_SURFACE.selected
    : isPending
      ? CARD_SURFACE.actionable
      : CARD_SURFACE.default;

  return (
    <div className={`rounded-lg border p-3 transition-colors sm:p-4 ${surface}`}>
      <div className="flex items-start gap-3">
        <Checkbox
          checked={isSelected}
          disabled={!isPending || isProcessing}
          onCheckedChange={() => onToggle(item.uuid)}
          className="mt-1"
          aria-label={`Selecionar ${item.name} - ${item.classification}`}
        />

        <div className="min-w-0 flex-1">
          <p
            className={`break-words font-medium ${
              isPending ? "text-foreground" : "text-foreground/80"
            }`}
          >
            {item.name} - {item.classification}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className={`${BADGE_BASE} ${BADGE_NEUTRAL}`}>
              {item.quotas} {plural(item.quotas, "cota recebida", "cotas recebidas")}
            </span>
            <span className={`${BADGE_BASE} ${badgeStatus(item.statusId)}`}>
              {item.statusName}
            </span>
            {isPreSale && (
              <span className={`${BADGE_BASE} ${BADGE_PRE_SALE}`}>Pré-venda</span>
            )}
          </div>

          <div className={`mt-3 rounded-lg border p-3 ${SURFACE_INSET}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Informações
            </p>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <InfoField label="Criado por">
                {item.createdByName || "Não informado"}
              </InfoField>
              {item.createdAt && (
                <InfoField label="Data de criação">
                  {formatDate(item.createdAt)}
                </InfoField>
              )}
              {showProcessedBy && (
                <InfoField
                  label={
                    isAccepted
                      ? "Aceito por"
                      : isRejected
                        ? "Rejeitado por"
                        : "Atualizado por"
                  }
                >
                  {isPreSale && isAccepted ? (
                    <span className="font-semibold text-amber-700 dark:text-amber-300">
                      {processedBy}
                    </span>
                  ) : (
                    processedBy
                  )}
                </InfoField>
              )}
              {item.updatedAt && (
                <InfoField label="Data">{formatDate(item.updatedAt)}</InfoField>
              )}
            </div>
          </div>

          {isLoadingFiles ? (
            <div className={`mt-3 flex items-center gap-2 rounded-lg border p-3 ${SURFACE_INSET}`}>
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Carregando fotos...
              </span>
            </div>
          ) : (
            files.length > 0 && (
              <Collapsible open={filesOpen} onOpenChange={setFilesOpen} className="mt-3">
                <CollapsibleTrigger
                  className={`flex w-full items-center gap-2 rounded-lg border p-2.5 text-left transition-colors hover:bg-muted dark:hover:bg-black/30 ${SURFACE_INSET}`}
                >
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">
                    {files.length} {plural(files.length, "foto", "fotos")} do bolão
                  </span>
                  <ChevronDown
                    className={`ml-auto h-4 w-4 text-muted-foreground transition-transform ${
                      filesOpen ? "rotate-180" : ""
                    }`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-1 space-y-1">
                  {files.map((file) => (
                    <a
                      key={file.id}
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 rounded border p-2 transition-colors hover:border-primary/50 ${SURFACE_CARD}`}
                    >
                      <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate text-sm text-foreground hover:underline">
                        {file.file_name}
                      </span>
                    </a>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            )
          )}
        </div>

        {isPending && (
          <div className="flex shrink-0 flex-col gap-1 sm:flex-row sm:gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={isProcessing}
              onClick={() => onAccept(item)}
              className={ACTION_BUTTON.acceptGhost}
            >
              <Check className="mr-1 h-4 w-4" />
              Aceitar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={isProcessing}
              onClick={() => onReject(item)}
              className={ACTION_BUTTON.rejectGhost}
            >
              <X className="mr-1 h-4 w-4" />
              Rejeitar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
